import { Routes, Route, useLocation } from 'react-router-dom';
import { AuthLayout } from '@/layouts/AuthLayout';
import { NotFoundPage } from '@/pages/NotFoundPage';
import NotificationListener from './components/Notification/NotificationListener';
import { Toaster } from 'react-hot-toast';
import SplashCursor from './components/SplashCursor';
import { useEffect, useLayoutEffect, useState, lazy, Suspense } from 'react';
import { initAuth } from './utils/initAuth';
import { useDispatch } from 'react-redux';
import { useLazyGetUserProfileQuery } from './store/api/userApi';
import { setAuthReady, setUserProfile } from './store/slices/authSlice';
import { tokenService } from './utils/tokenService';
import { logout } from './store';
import { useAuthReady } from './hooks/redux';
import { ScrollSmoother } from 'gsap/all';
import SplashLoader from './components/SplashLoader';
import { AuthPage } from './pages/auth/AuthPage';
import ProtectedRoute from './components/common/ProtectedRoute';
import NewVotes from './pages/Votes/NewVotes';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsConditions from './components/TermsConditions';

const Home = lazy(() => import('./components/Home'));
const LiveStream = lazy(() => import('./pages/LiveStream'));
const ProfilePage = lazy(() =>
  import('./pages/Profile/ProfilePage').then((m) => ({ default: m.ProfilePage }))
);
const Votes = lazy(() => import('./pages/Votes').then((m) => ({ default: m.Votes })));
const MintSection = lazy(() =>
  import('./components/Home/MintSection').then((m) => ({ default: m.MintSection }))
);
const Dashboard = lazy(() =>
  import('./components/Dashboard').then((m) => ({ default: m.Dashboard }))
);
const PrizeScreen = lazy(() =>
  import('./pages/PrizeScreen').then((m) => ({ default: m.PrizeScreen }))
);
const NftSection = lazy(() =>
  import('./components/Home/MintSection/NftSection').then((m) => ({ default: m.NftSection }))
);

const TimelinePage = lazy(() =>
  import('./components/Dashboard/TimelinePage').then((m) => ({ default: m.TimelinePage }))
);

const NftCards = lazy(() => import('./components/Home/MintSection/NftCards'));

export function App() {
  const dispatch = useDispatch();
  const [fetchUser] = useLazyGetUserProfileQuery();
  const authReady = useAuthReady();
  const location = useLocation();

  const [isSplashFinished, setIsSplashFinished] = useState(false);

  useEffect(() => {
    const bootstrap = async () => {
      const loggedIn = await initAuth();

      if (loggedIn) {
        try {
          const user = await fetchUser().unwrap();
          dispatch(setUserProfile(user));
        } catch (err) {
          tokenService.clear();
          dispatch(logout());
        }
      } else {
        dispatch(logout());
      }

      dispatch(setAuthReady(true));
    };

    bootstrap();
  }, []);

  useLayoutEffect(() => {
    if (location.pathname !== '/price') return;

    const smoother = ScrollSmoother.get();
    smoother?.scrollTo(0, false);
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const rafId = window.requestAnimationFrame(() => {
      smoother?.scrollTo(0, false);
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [location.pathname]);

  return (
    <>
      <NotificationListener />
      <SplashCursor />
      {(!authReady || !isSplashFinished) && (
        <SplashLoader onFinish={() => setIsSplashFinished(true)} isReady={authReady} />
      )}
      <Suspense fallback={<SplashLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/live" element={<LiveStream />} />

          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/votes" element={<NewVotes />} />
            {/* <Route path="/mintSection" element={<MintSection />} /> */}
            <Route path="/price" element={<PrizeScreen />} />
            <Route path="/mint-nft" element={<NftSection />} />
            <Route path="/nft-cards" element={<NftCards />} />
            <Route path="/time-line" element={<TimelinePage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 6000,
        }}
      />
    </>
  );
}
